import { getModel } from "../../model.mjs"
import { getOpenExchangeModel } from "../../openexchangemodel.mjs"
//import { parseString } from "xml2js"
import { XMLParser } from "fast-xml-parser"
import * as palette from "../../components/symbols/palette"
import _ from "lodash"
import * as dataServices from "./dataServices"
import * as urlServices from "./urlServices"
import * as chatPromptServices from "./chatPromptServices"
import * as cloudFunctions from "./cloudFunctions"
import { parseXmlToJson } from "./parseXmlToJson"
import {
  getStorage,
  ref,
  getDownloadURL,
  uploadString,
  listAll,
  deleteObject,
} from "firebase/storage"

const storeFile = async (path, fileInfo) => {
  const fullPath = `${path}/${fileInfo.name}`

  console.log(
    `%cstoring file %c[${fileInfo.name}]%c in path %c${path}, %cfullPath %c${fullPath}`,
    "color:yellow",
    "color:lightgreen",
    "color:white",
    "color:yellow",
    "color:white",
    "color:yellow"
  )

  const storageRef = ref(getStorage(), fullPath)
  const result = await uploadString(storageRef, fileInfo.modelText)
  console.log("%cstored file", "color:lightgreen", result)
}

const createModelCacheKey = (fileName, parentId, type) => {
  return { fileName, parentId, type }
}

const searchModelCache = ({ modelCacheKey, modelCache }) => {
  const cachedModel = Object.values(modelCache).find(
    (cacheEntry) =>
      cacheEntry.parent_id === modelCacheKey.parentId &&
      cacheEntry.model.file === modelCacheKey.fileName
    // TODO: Check if we can safely add this back in
    // && cacheEntry.type === modelCacheKey.type
  )
  return cachedModel
}

const deleteFile = async (path, fileName) => {
  const storageRef = ref(getStorage(), `${path}/${fileName}`)

  await deleteObject(storageRef)
}

const deleteAllFilesInFolder = async (path) => {
  //console.log("%cdelete files in folder", "color:orange", path)
  const folderRef = ref(getStorage(), path)

  await deleteObject(folderRef)
}

const getFileDetails = async (path) => {
  //console.log("getFileDetails", path)

  const storageRef = ref(getStorage(), path)

  const fileRefs = await listAll(storageRef)

  //console.log("fileRefs", fileRefs.items.length, fileRefs)

  const getFileInfo = fileRefs.items.map(async (fileRef) => {
    const meta = await fileRef.getMetadata()
    const downloadUrl = await getDownloadURL(fileRef)
    return { meta: meta, url: downloadUrl }
  })

  const fileUrlResults = await Promise.all(getFileInfo)

  return fileUrlResults
}

function traceIdInRawCompat(rawCompat, refId) {
  const results = []
  const folders = rawCompat?.["archimate:model"]?.folder || []
  for (const f of folders) {
    const fname = f?.$?.name
    const els = f?.element || []
    for (const el of els) {
      const a = el?.$
      if (a?.id === refId) {
        results.push({
          folder: fname,
          xsiType: a?.["xsi:type"],
          id: a?.id,
          name: a?.name || "",
        })
      }
    }
  }
  return results
}

const safeJson = (v) => {
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

function resolveElement(modelCacheItem, archimateElementId) {
  const el = modelCacheItem?.model?.elements?.find(
    (e) => e.id === archimateElementId
  )
  return el ? { id: el.id, name: el.name, type: el.type } : null
}

function logMissingBounds({ view, idx, ve, modelCacheItem, where }) {
  const diagType = ve?.diagramObject?.["xsi:type"]
  const diagramObjectId = ve?.diagramObject?.id
  const archimateElement = ve?.diagramObject?.archimateElement
  const resolved = archimateElement
    ? resolveElement(modelCacheItem, archimateElement)
    : null

  console.group(`[diag] Missing bounds @ ${where}`)
  console.log("view", { name: view?.name, id: view?.id })
  console.log("index", idx)
  console.log("diagramObject", {
    id: diagramObjectId,
    xsiType: diagType,
    archimateElement,
  })
  console.log("resolvedElement", resolved || "(none)")
  console.log("raw bounds preview", ve?.bounds ?? "(undefined)")
  console.groupEnd()
}

// 1) Verify every DiagramObject's archimateElement id resolves to a real element
function debugMissingDiagramRefs(model) {
  const elementIdSet = new Set(model.elements.map((e) => e.id))

  const problems = []
  for (const view of model.views) {
    for (const ve of view.elements) {
      // Notes / Groups / DiagramModelReference don't have underlying elements
      const t = ve.diagramObject?.["xsi:type"]
      const isViewOnly =
        t === `archimate:${palette.NOTE}` ||
        t === `archimate:${palette.GROUP}` ||
        t === `archimate:${palette.DIAGRAM_MODEL_REFERENCE}`

      if (isViewOnly) continue

      const refId = ve.diagramObject?.archimateElement
      if (!refId) {
        problems.push({ view: view.name, reason: "no-archimateElement", ve })
        continue
      }
      if (!elementIdSet.has(refId)) {
        problems.push({ view: view.name, reason: "missing-element", refId, ve })
      }
    }
  }

  if (problems.length) {
    console.group("[parse check] Unresolved view references")
    console.table(
      problems.map((p) => ({
        view: p.view,
        reason: p.reason,
        refId: p.refId || "",
        type: p.ve?.diagramObject?.["xsi:type"] || "",
        diagramObjectId: p.ve?.diagramObject?.id || "",
      }))
    )
    console.log("Sample problem object:", problems[0])
    console.groupEnd()
  } else {
    console.info("[parse check] All view references resolved ✅")
  }
  return problems
}

// 2) Verify every view element has bounds (without mutating data)
function debugMissingBounds(model) {
  const missing = []
  for (const view of model.views) {
    for (const ve of view.elements) {
      if (!ve || !ve.bounds) {
        missing.push({
          view: view.name,
          diagramObjectId: ve?.diagramObject?.id,
          diagramType: ve?.diagramObject?.["xsi:type"],
          archimateElement: ve?.diagramObject?.archimateElement,
        })
      }
    }
  }
  if (missing.length) {
    console.group("[parse check] View elements missing bounds")
    console.table(missing)
    console.groupEnd()
  } else {
    console.info("[parse check] All view elements have bounds ✅")
  }
  return missing
}

// parentId is either a project id or component id
// type is either "project" or "component"
const createModelIndexObject = async (
  model,
  fileName,
  parentId,
  type,
  accountId,
  hasAIMAIRole
) => {
  // 1) Element index
  const elementIndex = model.elements
    .filter((element) => element.name)
    .map((element) => ({
      name: element.name,
      id: element.id,
      type: palette.getIndex(element.type),
    }))
    .filter((item) => item.type !== -1)
    .sort((a, b) => a.name.localeCompare(b.name))

  // 2) Unique, case-insensitive list of element names
  const elementNames = Array.from(
    new Set(elementIndex.map((item) => item.name.toLowerCase()))
  )

  // 3) Cache item used to generate prompts
  const modelCacheItem = createModelCacheItem(
    model,
    fileName,
    model.name,
    parentId,
    type
  )

  // 4) STRICT OpenAI embeddings extractor
  const requireEmbedding = (res) => {
    if (
      !res ||
      !res.data ||
      !Array.isArray(res.data) ||
      !res.data[0] ||
      !Array.isArray(res.data[0].embedding)
    ) {
      const sample =
        res && typeof res === "object"
          ? JSON.stringify(res).slice(0, 600)
          : String(res)
      throw new Error(
        "[embeddings] Unexpected response shape from createEmbedding. " +
          "Expected res.data[0].embedding (number[]). Got: " +
          sample
      )
    }
    return res.data[0].embedding
  }

  // 5) Build embeddings for each view
  const viewEmbeddings = []

  const promptDataResults = model.views.map(async (view) => {
    const promptData = chatPromptServices.createPromptDataFromModelCache(
      modelCacheItem,
      view
    )

    // May return an ARRAY; force to a single string for embeddings
    const promptAny = chatPromptServices.createChatPrompt({
      promptData,
      promptLayers: palette.LAYERS.map((layer) => layer.name),
      includeProperties: true,
      includeDoco: true,
      includeIds: false,
    })

    const prompt = Array.isArray(promptAny)
      ? promptAny.join("\n")
      : String(promptAny)

    // Strict guards
    if (!prompt || !prompt.trim()) {
      throw new Error("[embeddings] prompt is empty")
    }
    const MAX_CHARS = 200000
    if (prompt.length > MAX_CHARS) {
      throw new Error(
        `[embeddings] prompt too long (${prompt.length} > ${MAX_CHARS})`
      )
    }

    // Create embedding (client wrapper must return { data: [ { embedding } ] })
    const res = await cloudFunctions.createEmbedding(prompt)
    const embedding = requireEmbedding(res)

    return {
      model: { name: model.name },
      view: { name: view.name, id: view.id },
      embedding,
    }
  })

  viewEmbeddings.push(...(await Promise.all(promptDataResults)))

  // 6) Final index object
  const index = {
    account_id: accountId,
    parent_id: parentId,
    model_id: model.id,
    type,
    file_name: fileName,
    index_version: 1,
    elements: elementIndex,
    view_embeddings: viewEmbeddings,
    names: elementNames,
    created: dataServices.localTimestamp(),
    modified: dataServices.localTimestamp(),
  }

  return index
}

function normalizeOpenExchangeShape(obj) {
  if (!obj?.model) return obj // not an openexchange file

  const mustBeArrays = [
    "name",
    "views",
    "diagrams",
    "view",
    "node",
    "connection",
    "elements",
    "element",
    "relationships",
    "relationship",
    "propertyDefinitions",
    "propertyDefinition",
    "properties",
    "property",
    "documentation",
    "value",
    "item",
    "label"
  ]

  const wrapArrays = (node) => {
    if (Array.isArray(node)) {
      node.forEach(wrapArrays)
    } else if (node && typeof node === "object") {
      for (const key of Object.keys(node)) {
        if (mustBeArrays.includes(key) && !Array.isArray(node[key])) {
          node[key] = [node[key]]
        }
        wrapArrays(node[key])
      }
    }
  }

  wrapArrays(obj.model)
  return obj
}


const loadFile = async (
  folderPath,
  fileName,
  setModelCallback,
  extraCallbackProps,
  setStatus
) => {
  try {
    const storageRef = ref(getStorage(), `${folderPath}/${fileName}`)
    const downloadUrl = await getDownloadURL(storageRef)

    const res = await fetch(downloadUrl)
    if (!res.ok) throw new Error(`Failed to fetch file: HTTP ${res.status}`)

    const rawText = await res.text()
    console.log("%craw file text", "color:lightgreen", { rawText })

    // 1) FXP options tuned for xml2js-like behavior
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "@",
      parseTagValue: false, // keep strings like xml2js
      parseAttributeValue: false, // keep strings like xml2js
    })

    let result
    try {
      result = parser.parse(rawText)
    } catch (e) {
      throw new Error(`XML parse failed: ${e.message}`)
    }
    console.log("%cparsed xml", "color:lightgreen", { result })

    // 2) Shim: FXP → xml2js shape
    function toXml2jsShape(node) {
      if (Array.isArray(node)) return node.map(toXml2jsShape)
      if (node && typeof node === "object") {
        const out = {}
        const attrs = {}
        for (const [k, v] of Object.entries(node)) {
          if (k.startsWith("@")) {
            attrs[k.slice(1)] = v // e.g. "xsi:type", "id", "name"
          } else if (k === "#text") {
            out._ = v
          } else {
            out[k] = toXml2jsShape(v)
          }
        }
        if (Object.keys(attrs).length) out.$ = attrs

        // Promote full qualified type for your el.type === DIAGRAM checks
        if (out.$ && out.$["xsi:type"] && !out.type)
          out.type = out.$["xsi:type"]

        // Ensure arrays where your code maps/reads:
        const mustBeArrays = [
          "folder",
          "element",
          "property",
          "documentation",
          "child",
          "bounds",
          "sourceConnection",
          "bendpoint",
          "content", // note content is used for notes
        ]
        for (const key of mustBeArrays) {
          if (out[key] && !Array.isArray(out[key])) out[key] = [out[key]]
        }
        return out
      }
      return node
    }

    const compat = toXml2jsShape(result)

    // Use file extension to pick parser (unchanged)
    const isArchiNative = !!compat["archimate:model"]
    const fileType = isArchiNative ? "archimate" : "openexchange"
    console.log("%cfile type", "color:lightgreen", { fileType })

    if (fileType === "archimate") {
      const model = await getModel(compat)
      console.log(
        "views?",
        model?.views?.length,
        model?.views?.map((v) => v.name)
      )
      debugMissingDiagramRefs(model)
      debugMissingBounds(model)


      await Promise.resolve(
        setModelCallback(model, fileName, rawText, { ...extraCallbackProps })
      )
    } else {
      normalizeOpenExchangeShape(compat)
      const model = await getOpenExchangeModel(compat)
      await Promise.resolve(
        setModelCallback(model, fileName, rawText, { ...extraCallbackProps })
      )
    }

    setStatus?.({ status: "ok" })
    return { status: "ok" }
  } catch (err) {
    console.error("[loadFile] error", err)
    setStatus?.({ status: "error", message: "Error loading model file", err })
    return { status: "error", err }
  }
}

const getPaletteItem = (type, caller) => {
  if (type) {
    const symbol = palette.symbols[type]
    if (symbol) {
      return symbol
    }
  }

  console.log("%ccannot find type", "color:orange", type, caller)

  return palette.GenericSymbol
}

const defaultNullBoundsToZero = (bounds) => {
  if (bounds.x === null) {
    console.log("%cBOUNDS NULL", "color:orange")
  }
  return {
    x: bounds.x || 0,
    y: bounds.y || 0,
    width: bounds.width || 0,
    height: bounds.height || 0,
  }
}

const getNode = (model, nodeId) => {
  return model.model.elements.find((element) => element.id === nodeId)
}

//TODO: detect any cyclic loops, e.g. where A -> B, and B -> A either directly, or through a longer chain of connections -- Need to exit if loop detected
const mapSourceAndTarget = (r, model, relationships) => ({
  ...r,
  sourceNode: () =>
    createGraphNode(getNode(model, r.source), relationships, model),
  targetNode: () =>
    createGraphNode(getNode(model, r.target), relationships, model),
})

const createGraphNode = (element, relationships, model) => {
  const edges_incoming = relationships
    .filter((r) => r.target === element.id)
    .map((r) => mapSourceAndTarget(r, model, relationships))

  const edges_outgoing = relationships
    .filter((r) => r.source === element.id)
    .map((r) => mapSourceAndTarget(r, model, relationships))

  //console.log("%ccreateGraphNode", "color:yellow", { element, edges_incoming, edges_outgoing })

  const elementType = palette.getElementType(element.type)
  if (!elementType) {
    return undefined
  } else {
    return {
      ...element,
      layer: elementType.layer.name,
      edges: {
        in: edges_incoming,
        out: edges_outgoing,
      },
    }
  }
}

const buildGraph = (models) => {
  //console.log("%cbuildGraph", "color:orange", models)

  const result = _.flatten(
    models.map((model) => {
      const relationships = model.model.elements.filter((e) => e.source)

      const elements = model.model.elements
        .filter((e) => !e.source)
        .map((e) => {
          return createGraphNode(e, relationships, model)
        })
        // This will filter out where creaateGraphNode returns
        // undefined, which happens when the element type is a relationnship
        // and not an element
        .filter((e) => e !== undefined)
      // .sort((a, b) => {
      //     if (a.layer !== b.layer) return a.layer.localeCompare(b.layer)
      //     return a.name.localeCompare(b.name)
      // })
      //console.log("%celements", "color:orange", elements)

      return elements
    })
  )

  return result
}

const getViewElements = (view, modelCacheItem) => {
  const typeName = "xsi:type"

  const fileName = modelCacheItem.model.file
  const parentId = modelCacheItem.parent_id

  const viewElements = view.elements.map((viewElement) => {
    if (viewElement.diagramObject[typeName] === `archimate:${palette.NOTE}`) {
      return {
        ...viewElement,
        element: undefined,
        symbol: getPaletteItem(palette.NOTE, "for Note"),
        symbolName: palette.NOTE,
      }
    }

    if (viewElement.diagramObject[typeName] === `archimate:${palette.GROUP}`) {
      return {
        ...viewElement,
        element: undefined,
        symbol: getPaletteItem(palette.GROUP, "for Group"),
        symbolName: palette.GROUP,
      }
    }

    if (
      viewElement.diagramObject[typeName] ===
      `archimate:${palette.DIAGRAM_MODEL_REFERENCE}`
    ) {
      // Get view name referenced by this element
      const referencedView = modelCacheItem.model.views.find(
        (view) => view.id === viewElement.diagramObject.model
      )

      const diagramModelRef = {
        ...viewElement,
        element: { name: `${referencedView.name}` },
        symbol: getPaletteItem(
          palette.DIAGRAM_MODEL_REFERENCE,
          "for diagram model reference"
        ),
        symbolName: palette.DIAGRAM_MODEL_REFERENCE,
        linkInfo: {
          url: urlServices.createViewEditUrl({
            parentId: parentId,
            viewId: referencedView.id,
            fileName: fileName,
          }),
          state: { parent: parentId, type: "???" },
        },
      }

      return diagramModelRef
    }

    const element = modelCacheItem.model.elements.find(
      (el) => el.id === viewElement.diagramObject.archimateElement
    )

    if (!element) {
      console.warn(
        `%cCannot find archimateElement %c${viewElement.diagramObject.archimateElement}`,
        "color:yellow",
        "color:pink",
        {
          type: viewElement.diagramObject[typeName],
          viewElement,
          modelElementsCount: modelCacheItem.model.elements.length,
        }
      )
      // Treat unresolved diagram items as view-only containers (Group-like)
      return {
        ...viewElement,
        element: undefined,
        symbol: getPaletteItem(
          palette.GROUP,
          "fallback for unresolved diagram object"
        ),
        symbolName: palette.GROUP,
        label: "GROUP CONTENT?",
      }
    }

    const url = urlServices.createElementViewUrl({
      parentId: parentId,
      fileName: fileName,
      elementId: element.id,
    })

    const getLabel = (element, viewElement) => {
      switch (element.type) {
        case "Note":
          return viewElement.noteContent

        case "Group":
          return "GROUP CONTENT?"

        default:
          return element ? element.name : "No name"
      }
    }

    const label = getLabel(element, viewElement)

    return {
      ...viewElement,
      element: element,

      // This 'symbol' attribuite wont show when we pretty print the JSON
      // hence why we've added 'symbolName' just for debugging purposes
      symbol: getPaletteItem(element.type, "getViewElements"),
      symbolName: element.type,

      label: label,

      linkInfo: {
        url: url,
      },
    }
  })

  //console.log('%cfound view elements', 'color:yellow', view.name, viewElements)

  return viewElements
}

const splitCamelCase = (str) => {
  str = str.charAt(0).toUpperCase() + str.slice(1) // Capitalize the first letter
  str = str.replace(/([0-9A-Z])/g, " $&") // Add space between camel casing
  return str
}

// Sometimes the numeric bendpoint data is read in as a string, which then causes error with any calcs
// So ensure bendpoint data is represented as ints
const bendpointToInts = (bp) => {
  return {
    // If startX or startY are 0 then the 'startX' and 'startY' attributes are not present
    startX: parseInt(bp.startX) || 0,
    startY: parseInt(bp.startY) || 0,
    endX: parseInt(bp.endX),
    endY: parseInt(bp.endY),
  }
}

const DIAG_PARSE = true

const safeStringify = (obj) => {
  const seen = new WeakSet()
  return JSON.stringify(
    obj,
    (k, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "[Circular]"
        seen.add(v)
      }
      return v
    },
    2
  )
}

function logViewElementsDiagnostics(view, elements) {
  try {
    const rows = elements.map((ve, i) => ({
      idx: i,
      ok: !!ve && !!ve.bounds,
      hasVE: !!ve,
      hasBounds: !!ve?.bounds,
      hasDiagramObject: !!ve?.diagramObject,
      diagType: ve?.diagramObject?.["xsi:type"] || "",
      archimateElement: ve?.diagramObject?.archimateElement || "",
      noteContentLen: Array.isArray(ve?.noteContent)
        ? ve.noteContent.length
        : ve?.noteContent
        ? 1
        : 0,
    }))
    const missing = rows.filter((r) => !r.hasVE || !r.hasBounds)
    console.group(`[diag] View "${view.name}" elements (${rows.length} items)`)
    console.table(rows)
    if (missing.length) {
      console.warn(
        `[diag] ${missing.length} element(s) missing or without bounds (showing up to 3):`
      )
      missing.slice(0, 3).forEach((r) => {
        const ve = elements[r.idx]
        console.log(
          `[diag] elements[${r.idx}] preview`,
          JSON.parse(
            safeStringify({
              diagramObject: ve?.diagramObject,
              bounds: ve?.bounds,
              noteContent: ve?.noteContent,
              viewProperties: ve?.viewProperties,
            })
          )
        )
      })
    }
    console.groupEnd()
  } catch (e) {
    console.warn("[diag] logViewElementsDiagnostics failed:", e)
  }
}

const calculateDiagramDimensions = (view, modelCacheItem) => {
  const elements = getViewElements(view, modelCacheItem)

  if (DIAG_PARSE) {
    // pre-scan and log the first offender with full context
    const badIdx = elements.findIndex((ve) => !ve || !ve.bounds)
    if (badIdx !== -1) {
      logMissingBounds({
        view,
        idx: badIdx,
        ve: elements[badIdx],
        modelCacheItem,
        where: "calculateDiagramDimensions(pre-scan)",
      })
    }
  }

  let cMinX, cMinY
  const diagramDimensions = calculateDiagramElementDimensions(
    elements,
    cMinX,
    cMinY
  )

  return { viewId: view.id, dimensions: diagramDimensions }
}
const calculateDiagramElementDimensions = (viewElements, cMinX, cMinY) => {
  let maxWidth = 0,
    maxHeight = 0
  let minX, minY
  if (cMinX) minX = cMinX
  if (cMinY) minY = cMinY

  viewElements.forEach((viewElement, idx) => {
    if (!viewElement || !viewElement.bounds) {
      // we don't have access to view/model here, so emit a focused line
      console.error(
        `[diag] calculateDiagramElementDimensions: viewElements[${idx}] is ${
          !viewElement ? "undefined" : "missing .bounds"
        }`
      )
      console.log("[diag] element preview", {
        diagramObjectId: viewElement?.diagramObject?.id,
        diagType: viewElement?.diagramObject?.["xsi:type"],
        archimateElement: viewElement?.diagramObject?.archimateElement,
        hasBounds: !!viewElement?.bounds,
        rawBounds: viewElement?.bounds,
      })
      // fall through to the existing failure (keeps behavior)
      // eslint-disable-next-line no-unused-expressions
      viewElement.bounds
    }

    const xVal = viewElement.bounds.x + viewElement.bounds.width
    maxWidth = Math.max(xVal, maxWidth)

    const yVal = viewElement.bounds.y + viewElement.bounds.height
    maxHeight = Math.max(maxHeight, yVal)

    if (minX === undefined || viewElement.bounds.x < minX) {
      minX = viewElement.bounds.x
    }
    if (minY === undefined || viewElement.bounds.y < minY) {
      minY = viewElement.bounds.y
    }
  })

  const margin = 0
  const dimensions = {
    width: maxWidth + margin - minX,
    height: maxHeight + margin - minY,
    widthOffset: minX,
    heightOffset: minY,
  }

  if (isNaN(dimensions.width) || isNaN(dimensions.height)) {
    console.log("%cNaN alert", "color:red", { dimensions, viewElements })
  }

  return dimensions
}

const getModelCacheId = ({ parentId, fileName }) => {
  return `${parentId}-${fileName}`
}

const getModelFromCache = ({ modelCache, parentId, fileName }) => {
  const modelCacheItem = Object.values(modelCache).find(
    (cacheEntry) =>
      cacheEntry.parent_id === parentId && cacheEntry.model.file === fileName
  )

  return modelCacheItem
}

/**
 * Add a model cache item into the model cache.
 *
 * @param {*} model
 * @param {*} fileName
 * @param {*} name
 * @param {*} parentId
 * @param {*} parentType
 * @returns
 */
const createModelCacheItem = (model, fileName, name, parentId, parentType) => {
  // console.log("%ccreateModelCacheItem", "color:yellow", {
  //     name,
  //     fileName,
  //     parentId,
  //     parentType,
  // })
  const modelState = {
    //id: `${model.id}-${fileName}`,
    id: getModelCacheId({ parentId, fileName }),
    parent_id: parentId,
    name: name,
    type: parentType,
    model: { ...model, file: fileName },
  }

  // Pre-calculate dimensions for all views, so we can cache this also for performance reasons
  const diagramDimensions = model.views.map((view) =>
    calculateDiagramDimensions(view, modelState)
  )

  const mergedViews = model.views.map((view) => ({
    ...view,
    dimensions: diagramDimensions.find((dim) => dim.viewId === view.id)
      .dimensions,
  }))

  const modelStateWithViewDimensions = {
    ...modelState,
    model: { ...modelState.model, views: mergedViews },
  }

  return modelStateWithViewDimensions
}

export {
  getFileDetails,
  loadFile,
  storeFile,
  deleteFile,
  deleteAllFilesInFolder,
  getViewElements,
  getPaletteItem,
  defaultNullBoundsToZero,
  splitCamelCase,
  calculateDiagramDimensions,
  createModelCacheItem,
  createModelCacheKey,
  searchModelCache,
  createModelIndexObject,
  bendpointToInts,
  getModelCacheId,
  buildGraph,
  createGraphNode,
  getModelFromCache,
}
